const MOBILE_QUERY = "(max-width: 767.98px)";
const NO_MOBILE_SELECTOR = ".table-responsive:not(.table-responsive-no-mobile):not(.table-responsive-schedules-fit)";
const ZOOM_ONLY_SELECTOR = ".table-responsive-no-mobile";
const SCHEDULES_FIT_SELECTOR = ".table-responsive-schedules-fit";
const MIN_SCALE = 0.25;
const MAX_SCALE = 3;
const DOUBLE_TAP_DELAY = 300;

const enhancers = new WeakMap();
const supportsZoom = typeof CSS !== "undefined" && CSS.supports("zoom", "1");

let clientZoomScale = null;
let schedulesFitScale = null;

const getTouchDistance = touches => Math.hypot(
  touches[0].clientX - touches[1].clientX,
  touches[0].clientY - touches[1].clientY
);

const getContentWidth = inner => {
  const tables = inner.querySelectorAll("table");
  if (!tables.length) return inner.scrollWidth;

  return Math.max(...Array.from(tables, table => table.scrollWidth));
};

const calculateFitScale = (container, inner) => {
  const containerWidth = container.clientWidth;
  const contentWidth = getContentWidth(inner);

  if (!containerWidth || !contentWidth || contentWidth <= containerWidth) {
    return 1;
  }

  return Math.max(MIN_SCALE, containerWidth / contentWidth);
};

const measureContentSize = inner => ({
  width: getContentWidth(inner),
  height: inner.scrollHeight,
});

const applyScale = (element, scale, baseSize) => {
  if (!element) return;

  if (supportsZoom) {
    element.style.zoom = scale;
    element.style.transform = "";
    element.style.width = "";
    element.style.height = "";
    element.style.position = "";
    element.style.top = "";
    element.style.left = "";
  } else {
    element.style.zoom = "";
    element.style.transform = `scale(${scale})`;
    element.style.transformOrigin = "top left";
    element.style.position = "absolute";
    element.style.top = "0";
    element.style.left = "0";
    element.style.width = `${baseSize.width}px`;
    element.style.height = `${baseSize.height}px`;
  }
};

const clearScale = element => {
  if (!element) return;

  element.style.zoom = "";
  element.style.transform = "";
  element.style.width = "";
  element.style.height = "";
  element.style.position = "";
  element.style.top = "";
  element.style.left = "";
};

const syncColumnWidths = (headerTable, bodyTable) => {
  const headerCells = headerTable.querySelectorAll("th");
  const firstBodyRow = bodyTable.querySelector("tbody tr");

  if (!headerCells.length || !firstBodyRow) return;

  const bodyCells = firstBodyRow.querySelectorAll("td, th");

  headerCells.forEach((headerCell, index) => {
    const bodyCell = bodyCells[index];
    if (!bodyCell) return;

    const width = Math.max(bodyCell.offsetWidth, headerCell.scrollWidth);
    headerCell.style.width = `${width}px`;
    headerCell.style.minWidth = `${width}px`;
    bodyCell.style.width = `${width}px`;
    bodyCell.style.minWidth = `${width}px`;
  });
};

const FLAGGED_JOBS_FROZEN_COLUMNS = 3;

const applyFrozenColumnsToTable = (table, widths, isHeader) => {
  table.querySelectorAll("tr").forEach(row => {
    const cells = row.querySelectorAll("th, td");
    let left = 0;

    cells.forEach((cell, index) => {
      cell.classList.remove("sticky-col", "sticky-col-last");
      cell.style.left = "";
      cell.style.zIndex = "";

      if (index >= FLAGGED_JOBS_FROZEN_COLUMNS) return;

      cell.classList.add("sticky-col");
      if (index === FLAGGED_JOBS_FROZEN_COLUMNS - 1) {
        cell.classList.add("sticky-col-last");
      }

      cell.style.left = `${left}px`;
      cell.style.zIndex = isHeader ? "5" : "2";
      left += widths[index] || cell.offsetWidth;
    });
  });
};

const syncFrozenColumns = (stickyHeaders, inner) => {
  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  const processTablePair = (bodyTable, headerTable) => {
    if (!bodyTable.classList.contains("flagged-jobs-table")) return;

    const measureRow = headerTable?.querySelector("thead tr") || bodyTable.querySelector("thead tr");
    if (!measureRow) return;

    const widths = Array.from(measureRow.querySelectorAll("th, td"))
      .slice(0, FLAGGED_JOBS_FROZEN_COLUMNS)
      .map(cell => cell.offsetWidth);

    if (headerTable) {
      applyFrozenColumnsToTable(headerTable, widths, true);
    }

    applyFrozenColumnsToTable(bodyTable, widths, false);
  };

  if (stickyHeaders.length) {
    stickyHeaders.forEach(({ headerTable, bodyTable }) => {
      processTablePair(bodyTable, headerTable);
    });
    return;
  }

  inner.querySelectorAll("table.flagged-jobs-table").forEach(bodyTable => {
    processTablePair(bodyTable, null);
  });
};

const setupStickyHeaders = (container, inner, sizer) => {
  const stickyHeaders = [];

  inner.querySelectorAll("table").forEach(bodyTable => {
    const thead = bodyTable.querySelector("thead");
    if (!thead || bodyTable.dataset.stickyHeaderReady === "true") return;

    const headerPane = document.createElement("div");
    headerPane.className = "table-responsive-zoomable__sticky-header";

    const headerTable = document.createElement("table");
    headerTable.className = bodyTable.className;
    headerTable.appendChild(thead);

    ["mt-4", "mt-3", "mt-2"].forEach(className => {
      bodyTable.classList.remove(className);
    });

    headerPane.appendChild(headerTable);
    container.insertBefore(headerPane, sizer);

    bodyTable.dataset.stickyHeaderReady = "true";
    stickyHeaders.push({ headerPane, headerTable, bodyTable });
  });

  return stickyHeaders;
};

const restoreStickyHeaders = stickyHeaders => {
  stickyHeaders.forEach(({ headerPane, headerTable, bodyTable }) => {
    const thead = headerTable.querySelector("thead");
    if (thead) {
      bodyTable.insertBefore(thead, bodyTable.firstChild);
    }

    delete bodyTable.dataset.stickyHeaderReady;
    headerPane.remove();
  });
};

const updateScrollArea = (container, inner, scale, stickyHeaders, { syncColumns = true, previousScale = null, zoomOnly = false, fitToViewOnly = false } = {}) => {
  const sizer = container.querySelector(".table-responsive-zoomable__sizer");
  const scrollLeft = container.scrollLeft;
  const scrollTop = container.scrollTop;
  const baseSize = measureContentSize(inner);

  applyScale(inner, scale, baseSize);
  stickyHeaders.forEach(({ headerPane }) => {
    applyScale(headerPane, scale, baseSize);
  });

  if (syncColumns) {
    stickyHeaders.forEach(({ headerTable, bodyTable }) => {
      syncColumnWidths(headerTable, bodyTable);
    });
  }

  if (!sizer) return;

  const scaledHeight = Math.ceil(baseSize.height * scale);
  const scaledWidth = Math.ceil(baseSize.width * scale);

  if (supportsZoom) {
    sizer.style.width = fitToViewOnly ? `${scaledWidth}px` : "";
    sizer.style.height = fitToViewOnly ? `${scaledHeight}px` : "";
  } else {
    sizer.style.width = `${scaledWidth}px`;
    sizer.style.height = `${scaledHeight}px`;
  }

  if (fitToViewOnly) {
    container.style.height = `${scaledHeight}px`;
  } else {
    container.style.height = "";
  }

  if (previousScale && previousScale !== scale) {
    const ratio = scale / previousScale;
    container.scrollLeft = scrollLeft * ratio;
    container.scrollTop = scrollTop * ratio;
  }

  if (!zoomOnly && !fitToViewOnly) {
    syncFrozenColumns(stickyHeaders, inner);
  }
};

const enhanceContainer = (container, { zoomOnly = false, fitToViewOnly = false } = {}) => {
  if (container.dataset.zoomEnhanced === "true") return;
  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  container.dataset.zoomEnhanced = "true";
  container.dataset.zoomMode = fitToViewOnly ? "schedules-fit" : (zoomOnly ? "manual" : "fit");
  container.classList.add("table-responsive-zoomable");

  let sizer = container.querySelector(".table-responsive-zoomable__sizer");
  let inner = container.querySelector(".table-responsive-zoomable__inner");

  if (!sizer || !inner) {
    inner = document.createElement("div");
    inner.className = "table-responsive-zoomable__inner";

    while (container.firstChild) {
      inner.appendChild(container.firstChild);
    }

    sizer = document.createElement("div");
    sizer.className = "table-responsive-zoomable__sizer";
    sizer.appendChild(inner);
    container.appendChild(sizer);
  }

  const stickyHeaders = (zoomOnly || fitToViewOnly) ? [] : setupStickyHeaders(container, inner, sizer);

  const state = {
    scale: 1,
    fitScale: 1,
    pinchStart: { distance: 0, scale: 1 },
    lastTap: 0,
    inner,
    sizer,
    stickyHeaders,
    isPinching: false,
    zoomOnly,
    fitToViewOnly,
  };

  const setScale = (nextScale, options = {}) => {
    const previousScale = state.scale;
    state.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
    if (!zoomOnly && !fitToViewOnly) {
      clientZoomScale = state.scale;
    }
    updateScrollArea(container, inner, state.scale, stickyHeaders, {
      previousScale,
      zoomOnly,
      fitToViewOnly,
      ...options,
    });
  };

  const syncStickyColumns = () => {
    if (zoomOnly || fitToViewOnly) return;

    stickyHeaders.forEach(({ headerTable, bodyTable }) => {
      syncColumnWidths(headerTable, bodyTable);
    });
    syncFrozenColumns(stickyHeaders, inner);
  };

  const applyFitScale = () => {
    state.fitScale = calculateFitScale(container, inner);
    setScale(state.fitScale);
  };

  const applyInitialScale = () => {
    if (zoomOnly) {
      setScale(1);
      return;
    }

    state.fitScale = calculateFitScale(container, inner);

    if (fitToViewOnly) {
      if (schedulesFitScale !== null) {
        setScale(schedulesFitScale);
      } else {
        setScale(state.fitScale);
      }
      return;
    }

    if (clientZoomScale !== null) {
      setScale(clientZoomScale);
    } else {
      setScale(state.fitScale);
    }
  };

  const blockGesture = event => {
    event.preventDefault();
  };

  const onTouchStart = event => {
    if (event.touches.length === 2) {
      event.preventDefault();
      container.classList.add("is-pinching");
      state.isPinching = true;
      state.pinchStart = {
        distance: getTouchDistance(event.touches),
        scale: state.scale,
      };
    }
  };

  const onTouchMove = event => {
    if (event.touches.length !== 2) return;

    event.preventDefault();

    const { distance, scale: startScale } = state.pinchStart;
    if (!distance) return;

    const nextDistance = getTouchDistance(event.touches);
    setScale(startScale * (nextDistance / distance), { syncColumns: false });
  };

  const onTouchEnd = event => {
    const wasPinching = state.isPinching;

    if (event.touches.length < 2) {
      state.isPinching = false;
      state.pinchStart = { distance: 0, scale: state.scale };
      container.classList.remove("is-pinching");

      if (wasPinching) {
        syncStickyColumns();
        return;
      }
    }

    if (event.changedTouches.length !== 1) return;

    const now = Date.now();
    if (now - state.lastTap < DOUBLE_TAP_DELAY) {
      if (zoomOnly) {
        if (Math.abs(state.scale - 1) > 0.05) {
          setScale(1);
        }
      } else if (Math.abs(state.scale - state.fitScale) < 0.05) {
        setScale(1);
      } else {
        applyFitScale();
      }
      state.lastTap = 0;
      return;
    }

    state.lastTap = now;
  };

  if (!fitToViewOnly) {
    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchcancel", onTouchEnd, { passive: true });
    container.addEventListener("gesturestart", blockGesture, { passive: false });
    container.addEventListener("gesturechange", blockGesture, { passive: false });
    container.addEventListener("gestureend", blockGesture, { passive: false });

    state.listeners = [
      ["touchstart", onTouchStart],
      ["touchmove", onTouchMove],
      ["touchend", onTouchEnd],
      ["touchcancel", onTouchEnd],
      ["gesturestart", blockGesture],
      ["gesturechange", blockGesture],
      ["gestureend", blockGesture],
    ];
  }

  let resizeFrame;
  state.resizeObserver = new ResizeObserver(() => {
    if (state.isPinching) return;
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      if (zoomOnly) {
        updateScrollArea(container, inner, state.scale, stickyHeaders, { zoomOnly });
        return;
      }

      if (fitToViewOnly) {
        state.fitScale = calculateFitScale(container, inner);
        const scale = schedulesFitScale !== null ? schedulesFitScale : state.fitScale;
        setScale(scale);
        return;
      }

      const previousFitScale = state.fitScale;
      state.fitScale = calculateFitScale(container, inner);

      if (clientZoomScale === null && Math.abs(state.scale - previousFitScale) < 0.05) {
        setScale(state.fitScale);
      } else {
        updateScrollArea(container, inner, state.scale, stickyHeaders);
      }
    });
  });
  state.resizeObserver.observe(inner);

  requestAnimationFrame(applyInitialScale);
  enhancers.set(container, state);
};

const destroyContainer = container => {
  const state = enhancers.get(container);
  if (!state) return;

  state.listeners?.forEach(([event, handler]) => {
    container.removeEventListener(event, handler);
  });
  state.resizeObserver?.disconnect();

  restoreStickyHeaders(state.stickyHeaders || []);

  const sizer = container.querySelector(".table-responsive-zoomable__sizer");
  const inner = sizer?.querySelector(".table-responsive-zoomable__inner") || state.inner;

  if (inner) {
    clearScale(inner);
    while (inner.firstChild) {
      container.appendChild(inner.firstChild);
    }
    inner.remove();
  }

  clearScale(sizer);
  sizer?.remove();
  container.style.height = "";
  container.classList.remove("table-responsive-zoomable", "is-pinching");
  delete container.dataset.zoomEnhanced;
  delete container.dataset.zoomMode;
  enhancers.delete(container);
};

let scanTimeout;
let mutationObserver;

const scanAndEnhance = (root = document) => {
  const isMobile = window.matchMedia(MOBILE_QUERY).matches;

  root.querySelectorAll(NO_MOBILE_SELECTOR).forEach(container => {
    if (isMobile) {
      enhanceContainer(container);
    } else if (container.dataset.zoomEnhanced === "true") {
      destroyContainer(container);
    }
  });

  root.querySelectorAll(ZOOM_ONLY_SELECTOR).forEach(container => {
    if (isMobile) {
      enhanceContainer(container, { zoomOnly: true });
    } else if (container.dataset.zoomEnhanced === "true") {
      destroyContainer(container);
    }
  });

  root.querySelectorAll(SCHEDULES_FIT_SELECTOR).forEach(container => {
    if (isMobile) {
      enhanceContainer(container, { fitToViewOnly: true });
    } else if (container.dataset.zoomEnhanced === "true") {
      destroyContainer(container);
    }
  });
};

const scheduleScan = () => {
  clearTimeout(scanTimeout);
  scanTimeout = setTimeout(() => scanAndEnhance(), 50);
};

const applyFitScaleToContainer = container => {
  const state = enhancers.get(container);
  if (!state) return null;

  state.fitScale = calculateFitScale(container, state.inner);
  state.scale = state.fitScale;
  updateScrollArea(container, state.inner, state.fitScale, state.stickyHeaders);

  return state.fitScale;
};

export const refreshPageTableZoom = (root = document) => {
  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  scanAndEnhance(root);
};

export const fitSchedulesTablesToView = (root = document) => {
  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  schedulesFitScale = null;
  scanAndEnhance(root);

  requestAnimationFrame(() => {
    const containers = Array.from(root.querySelectorAll(SCHEDULES_FIT_SELECTOR));

    containers.forEach(container => {
      if (container.dataset.zoomEnhanced !== "true") {
        enhanceContainer(container, { fitToViewOnly: true });
      }
    });

    const fitScales = containers
      .map(container => applyFitScaleToContainer(container))
      .filter(scale => scale !== null);

    if (!fitScales.length) return;

    const pageFitScale = Math.max(MIN_SCALE, Math.min(...fitScales));
    schedulesFitScale = pageFitScale;

    containers.forEach(container => {
      const state = enhancers.get(container);
      if (!state) return;

      state.scale = pageFitScale;
      updateScrollArea(container, state.inner, pageFitScale, state.stickyHeaders, { fitToViewOnly: true });
    });
  });
};

export const fitPageTablesToView = (root = document) => {
  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  clientZoomScale = null;
  scanAndEnhance(root);

  requestAnimationFrame(() => {
    const containers = Array.from(root.querySelectorAll(NO_MOBILE_SELECTOR));

    containers.forEach(container => {
      if (container.dataset.zoomEnhanced !== "true") {
        enhanceContainer(container);
      }
    });

    const fitScales = containers
      .map(container => applyFitScaleToContainer(container))
      .filter(scale => scale !== null);

    if (!fitScales.length) return;

    const pageFitScale = Math.max(MIN_SCALE, Math.min(...fitScales));
    clientZoomScale = pageFitScale;

    containers.forEach(container => {
      const state = enhancers.get(container);
      if (!state) return;

      state.scale = pageFitScale;
      updateScrollArea(container, state.inner, pageFitScale, state.stickyHeaders);
    });
  });
};

export const initTableZoomEnhancer = () => {
  scanAndEnhance();

  mutationObserver = new MutationObserver(scheduleScan);
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener("resize", scheduleScan);
  window.addEventListener("orientationchange", () => {
    setTimeout(scanAndEnhance, 300);
  });
};

export const destroyTableZoomEnhancer = () => {
  mutationObserver?.disconnect();
  clearTimeout(scanTimeout);
  document.querySelectorAll(".table-responsive[data-zoom-enhanced]").forEach(destroyContainer);
};
